import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useAsyncEffect from 'use-async-effect';
import { FiArrowLeft, FiGrid, FiCopy, FiUpload, FiCheck, FiPlus } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { Template } from '../../../model/Template';
import api from '../../../api/api';
import { to } from '../../../util/util';
import { Thumbnail, welcomeTemplateName, editPath } from './EmailTemplates';

type Source = 'layouts' | 'yours' | 'import';
type Layout = 'announcement' | 'campaign' | 'welcome' | 'blank';

// keys match Constants.TemplateType* / the blank base on the server
const layouts: { id: Layout; name: string; desc: string }[] = [
    { id: 'announcement', name: 'Announcement', desc: 'Headline, the summary and a button to the PDF. Investi fills it in for every ASX announcement.' },
    { id: 'campaign', name: 'Newsletter', desc: 'Your own words between a branded header and footer. For campaigns you write yourself.' },
    { id: 'welcome', name: 'Welcome email', desc: 'Sent when you invite a holder to the Website Hub. Only one per company.' },
    { id: 'blank', name: 'Blank', desc: 'Start with nothing and build it yourself.' },
];

// schematic thumbnails, as in the spec (not the real HTML)
const Sketch: React.FC<{ layout: Layout }> = ({ layout }) =>
    <div className={`sketch ${layout}`}>
        <div className="bar"><span /><span /></div>
        {layout === 'blank'
            ? <div className="plus"><FiPlus /></div>
            : <div className="body">
                <i className="h" />
                <i /><i /><i className="short" />
                {layout === 'announcement' && <><b className="img" /><i /><i className="short" /><b className="btn" /></>}
                {layout === 'campaign' && <><i /><i /><i className="short" /></>}
                {layout === 'welcome' && <><i /><b className="btn" /></>}
            </div>}
        <div className="foot"><i /><i className="short" /></div>
    </div>;

// spec 2:56080 "Start a New Template". Layouts trimmed to the bases we have (user decision 2026-10-13);
// "Blank Email" in the side list dropped (the Blank layout covers it). Creating opens the new template in the builder.
const NewTemplate: React.FC<{ onChanged: () => void }> = ({ onChanged }) => {
    const history = useHistory();
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [name, setName] = useState<string | null>(null); // null until typed: follows the picked layout's name
    const [source, setSource] = useState<Source>('layouts');
    const [layout, setLayout] = useState<Layout>('announcement');
    const [copyOf, setCopyOf] = useState('');
    const [importHtml, setImportHtml] = useState('');
    const [templates, setTemplates] = useState<Template[] | null>(null);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState('');

    useAsyncEffect(async isMounted => {
        if (!stock?.id) return;
        setTemplates(null);
        const [, result] = await to(api().getTemplates(stock.id));
        if (isMounted()) setTemplates(result?.templates ?? []);
    }, [stock?.id]);

    const hasWelcome = !!templates?.some(t => t.name === welcomeTemplateName);
    const isWelcome = source === 'layouts' && layout === 'welcome';
    const layoutName = source === 'layouts' ? layouts.find(l => l.id === layout)?.name ?? '' : '';
    const shownName = isWelcome ? welcomeTemplateName : name ?? layoutName;
    const ready = !!shownName.trim() && (source === 'layouts' ? !(isWelcome && hasWelcome) : source === 'yours' ? !!copyOf : !!importHtml.trim());
    const back = () => history.push('/communications/email-templates');

    const create = async () => {
        setCreating(true);
        setError('');
        const [err, result] = await to(api().createEmailTemplate({
            stockId: stock.id!,
            name: shownName,
            templateType: source === 'layouts' ? layout : undefined,
            copyOfId: source === 'yours' ? copyOf : undefined,
            html: source === 'import' ? importHtml : undefined,
        }));
        if (err || !result.templateId) {
            setCreating(false);
            setError(err?.message || result?.errorMessage || 'Failed to create the template');
            return;
        }
        onChanged();
        history.push(editPath(result.templateId));
    };

    const sources: [Source, React.ReactNode, string, string][] = [
        ['layouts', <FiGrid />, 'Investi Layouts', `${layouts.length} ready-made starting points`],
        ['yours', <FiCopy />, 'Your Templates', templates ? `${templates.length} you already have` : 'Loading…'],
    ];

    return <div className="email-templates new-template">
        <button type="button" className="link back" onClick={back}><FiArrowLeft /> Back to Email templates</button>
        <div className="page-header">
            <div>
                <h1>Start a New Template</h1>
                <p>Pick a layout to start from. You can change everything afterwards — the layout only decides where things sit.</p>
            </div>
            <div className="actions">
                <button type="button" className="btn-outline" onClick={back}>Cancel</button>
                <button type="button" className="btn-primary" disabled={!ready || creating} onClick={create}>{creating ? 'Creating…' : 'Create the Template'}</button>
            </div>
        </div>

        {error && <p className="error">{error}</p>}
        <div className="card name-card">
            <label className="field">
                <span className="label">Template name</span>
                <input value={shownName} disabled={isWelcome} placeholder="e.g. Quarterly update" onChange={e => setName(e.target.value)} />
            </label>
            {isWelcome && <p className="hint">The welcome email is always called “{welcomeTemplateName}” — that is how Investi finds it.</p>}
        </div>

        <div className="columns">
            <div className="card side">
                <div className="cap">Start from</div>
                {sources.map(([id, icon, title, sub]) => <button key={id} type="button" className={`src${source === id ? ' active' : ''}`} onClick={() => setSource(id)}>
                    <span className="ic">{icon}</span>
                    <span><span className="t">{title}</span><span className="s">{sub}</span></span>
                </button>)}
                <div className="import">
                    <div className="cap">Have a design already?</div>
                    <p>Paste the HTML your agency built and we will load it in for you.</p>
                    <button type="button" className={`btn-outline sm${source === 'import' ? ' active' : ''}`} onClick={() => setSource('import')}><FiUpload /> Import a Design</button>
                </div>
            </div>

            <div className="main">
                {source === 'layouts' && <>
                    <div className="main-head"><h2>Ready-made layouts</h2><span>Built by Investi for ASX-listed companies</span></div>
                    <div className="choices">
                        {layouts.map(l => {
                            const taken = l.id === 'welcome' && hasWelcome;
                            return <button key={l.id} type="button" disabled={taken} className={`choice${layout === l.id ? ' on' : ''}`} onClick={() => setLayout(l.id)}>
                                <Sketch layout={l.id} />
                                <span className="row"><span className="name">{l.name}</span><span className="radio">{layout === l.id && <FiCheck />}</span></span>
                                <span className="desc">{taken ? 'You already have a welcome email — edit that one instead.' : l.desc}</span>
                            </button>;
                        })}
                    </div>
                </>}

                {source === 'yours' && <>
                    <div className="main-head"><h2>Copy one of yours</h2><span>The new template starts as a copy — the original stays as it is</span></div>
                    {!templates && <p className="loading">Loading templates…</p>}
                    {templates?.length === 0 && <p className="empty">No templates yet — start from a layout instead.</p>}
                    <div className="choices">
                        {templates?.map(t => <button key={t.id!} type="button" className={`choice${copyOf === t.id ? ' on' : ''}`} onClick={() => setCopyOf(t.id!)}>
                            <Thumbnail html={t.html} />
                            <span className="row"><span className="name">{t.name}</span><span className="radio">{copyOf === t.id && <FiCheck />}</span></span>
                        </button>)}
                    </div>
                </>}

                {source === 'import' && <>
                    <div className="main-head"><h2>Import a design</h2><span>It must include an unsubscribe link: {'{{list_unsubscribe}}'}</span></div>
                    <textarea className="paste" rows={18} spellCheck={false} placeholder="Paste the full email HTML here, from <html> to </html>" value={importHtml} onChange={e => setImportHtml(e.target.value)} />
                </>}
            </div>
        </div>
    </div>;
};

export default NewTemplate;
